import { Link, useLocation, useNavigate } from "react-router-dom";
import { CalendarDays, Menu, X, LogOut, Sparkles, PanelLeftOpen, PanelLeftClose, Settings, MessageSquare } from "lucide-react";
import { useState } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/context/AuthContext";
import { useSidebarContext } from "@/context/SidebarContext";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import FaceAvatar from "@/components/FaceAvatar";

const lightLogoSrc = "/jilu%20logo2.svg";
const darkLogoSrc = "/jilu%20logo2%20dark.svg";

// This component is only used for web navigation (mobile web and desktop),
// not for iOS native app which uses bottom tabs
const Navbar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isCollapsed, setIsCollapsed } = useSidebarContext();
  const { theme } = useTheme();
  const { user, signOut } = useAuth();
  const logoSrc = theme === "dark" ? darkLogoSrc : lightLogoSrc;

  const navItems = [{
    path: "/chat",
    label: "Chat",
    icon: <MessageSquare className="h-5 w-5" />
  }, {
    path: "/logs",
    label: "Logs",
    icon: <CalendarDays className="h-5 w-5" />
  }, {
    path: "/insights",
    label: "Insights",
    icon: <Sparkles className="h-5 w-5" />
  }, {
    path: "/settings",
    label: "Settings",
    icon: <Settings className="h-5 w-5" />
  }];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/", { replace: true, state: { fromLogout: true } });
  };

  // Mobile Web Navigation
  if (isMobile) {
    return <>
        {/* Mobile Header */}
        <header className="fixed top-0 left-0 right-0 bg-white dark:bg-background shadow-md flex items-center justify-between px-4 z-20" 
                style={{ 
                  paddingTop: 'max(16px, env(safe-area-inset-top))',
                  height: 'calc(64px + env(safe-area-inset-top))'
                }}>
          <div className="flex items-center space-x-3">
            <button onClick={toggleMenu} className="p-2 rounded-md hover:bg-gray-100 dark:hover:bg-primary/10">
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
            <div className="flex items-center space-x-2">
              <img
                src={logoSrc}
                alt="jilu"
                className="h-5 w-auto shrink-0"
              />
              <span className="px-1.5 py-0.5 text-xs font-semibold text-gray-600 dark:text-primary-foreground/70 bg-gray-100 dark:bg-primary/10 rounded border border-gray-200 dark:border-primary/20">
                BETA
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {user && (
              <Button variant="ghost" size="sm" onClick={handleSignOut}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            )}
          </div>
        </header>

        {/* Mobile Sidebar */}
        {isMenuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-10" onClick={toggleMenu}>
            <aside className="absolute left-0 bg-white dark:bg-background shadow-md z-20" 
                  style={{ 
                    top: 'calc(64px + env(safe-area-inset-top))',
                    height: `calc(100vh - 64px - env(safe-area-inset-top))`,
                    width: '16rem'
                  }} 
                  onClick={e => e.stopPropagation()}>
              <nav className="p-4">
                <ul className="space-y-2">
                  {navItems.map(item => <li key={item.path}>
                      <Link to={item.path} className={`flex items-center space-x-3 px-4 py-3 rounded-md transition-colors ${location.pathname === item.path ? "bg-primary text-white dark:bg-primary/50 dark:text-primary-foreground" : "text-gray-600 dark:text-foreground/80 hover:bg-gray-100 dark:hover:bg-primary/10"}`} onClick={toggleMenu}>
                        {item.icon}
                        <span>{item.label}</span>
                      </Link>
                    </li>)}
                </ul>
                {user && (
                  <div className="border-t border-gray-200 dark:border-primary/20 mt-4 pt-4">
                    <div className="px-4 py-2">
                      <p className="text-sm font-medium text-gray-900 dark:text-foreground truncate">{user.name || user.email}</p>
                    </div>
                  </div>
                )}
              </nav>
            </aside>
          </div>}
      </>;
  }

  // Desktop Web Navigation 
  return <aside className={`bg-white dark:bg-background dark:border-r dark:border-primary/20 shadow-md h-screen fixed left-0 top-0 z-10 transition-all duration-300 overflow-y-auto ${isCollapsed ? 'w-16' : 'w-64'}`}>
      <div className={`border-b dark:border-primary/20 flex items-center ${isCollapsed ? 'justify-center p-3' : 'justify-between p-4'}`}>
        {!isCollapsed && (
          <div className="flex items-center space-x-2 pl-4">
            <img
              src={logoSrc}
              alt="jilu"
              className="h-[22px] w-auto shrink-0"
            />
            <span className="px-1.5 py-0.5 text-xs font-semibold text-gray-600 dark:text-primary-foreground/70 bg-gray-100 dark:bg-primary/10 rounded border border-gray-200 dark:border-primary/20">
              BETA
            </span>
          </div>
        )}
        <button 
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-primary/10 transition-colors"
          title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
        >
          {isCollapsed ? <PanelLeftOpen className="h-5 w-5" /> : <PanelLeftClose className="h-5 w-5" />}
        </button>
      </div>
      
      <nav className={`${isCollapsed ? 'p-2' : 'p-4'}`}>
        <ul className="space-y-2">
          {navItems.map(item => <li key={item.path}>
              <Link 
                to={item.path} 
                className={`flex items-center ${isCollapsed ? 'justify-center py-3 w-full' : 'space-x-3 px-4 py-3'} rounded-md transition-colors ${location.pathname === item.path ? "bg-primary text-white dark:bg-primary/50 dark:text-primary-foreground" : "text-gray-600 dark:text-foreground/80 hover:bg-gray-100 dark:hover:bg-primary/10"}`}
                title={isCollapsed ? item.label : undefined}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                {!isCollapsed && <span>{item.label}</span>}
              </Link>
            </li>)}
        </ul>
      </nav>

      {user && (
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-200 dark:border-primary/20 p-4">
          {!isCollapsed ? (
            <>
              <Link to="/profile" className="flex items-center space-x-2 mb-4 hover:bg-gray-100 dark:hover:bg-primary/10 p-2 rounded-md transition-colors">
                <div className="flex-shrink-0">
                  <FaceAvatar size={32} />
                </div>
                <div className="overflow-hidden">
                  <p className="text-sm font-medium truncate">{user.name || user.email}</p>
                </div>
              </Link>
              <Button variant="outline" className="w-full" onClick={handleSignOut}>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </>
          ) : (
            <div className="flex flex-col items-center space-y-2">
              <Link
                to="/profile"
                className="flex items-center justify-center hover:bg-gray-100 dark:hover:bg-primary/10 rounded-md transition-colors p-1"
                title="Profile"
              >
                <FaceAvatar size={32} />
              </Link>
              <Button
                variant="outline"
                size="icon"
                className="w-10 h-10"
                onClick={handleSignOut}
                title="Logout"
              >
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}
    </aside>;
};

export default Navbar;
